import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import "./index.css";
import "./theme/theme.scss";
import { buildRuntime } from "./react/runtime/buildRuntime";
import { RuntimeProvider } from "./react/runtime/RuntimeContext";
import { discoverClientActions } from "./managers/clientActionsDiscovery";
import { QueryProvider } from "./react/providers/QueryProvider";
import { LocaleProvider } from "./react/providers/LocaleProvider";
import { ThemeProvider } from "./theme/ThemeProvider";
import { TooltipProvider } from "./components/ui/tooltip";
import { Toaster } from "./components/ui/toaster";
import { createAppRouter } from "./react/router";

// Composition root: build the framework-agnostic runtime, then mount the React
// adapter (providers) around the router. Swap this file + src/react to retarget
// another view framework; src/core stays untouched.
// Enhancement 08 — glob-based client-action discovery. Picks up every
// `frontend/client-actions/*.tsx` plugin under foundation + domain
// trees at boot. Runs BEFORE buildRuntime so the bundled-default
// registrations + any glob-discovered registrations are both in place
// before the router mounts (router routes may dispatch client actions
// during initial render). Duplicate-command collisions throw with
// both source paths named — same loud-fail discipline the backend's
// model registry uses.
discoverClientActions();

const runtime = buildRuntime({
    onUnauthorized: () => {
        if (typeof window === "undefined") return;
        // Token expired mid-session: bounce to login but remember where we were
        // (skip auth pages so we never redirect back to a login screen).
        const here = window.location.pathname + window.location.search;
        const onAuthPage = window.location.pathname.startsWith("/auth/");
        window.location.assign(onAuthPage ? "/auth/login" : `/auth/login?redirect=${encodeURIComponent(here)}`);
    },
});

const router = createAppRouter(runtime);

createRoot(document.getElementById("root")!).render(
    <StrictMode>
        <RuntimeProvider runtime={runtime}>
            <ThemeProvider>
                <QueryProvider>
                    <LocaleProvider>
                        <TooltipProvider>
                            <RouterProvider router={router} />
                            <Toaster />
                        </TooltipProvider>
                    </LocaleProvider>
                </QueryProvider>
            </ThemeProvider>
        </RuntimeProvider>
    </StrictMode>,
);
